import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  OrganizationOfficeDto,
  OrganizationOfficeRequestDto,
} from 'src/api/FiretrolApiClient';
import { officeApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { invalidateFor } from 'src/api/queries/invalidationMap';
import { optimisticList, rollbackList } from 'src/api/queries/optimistic';

export function useCreateOffice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: OrganizationOfficeRequestDto) =>
      officeApiClient.create(request),
    meta: { successMessage: 'Office Created!', suppressErrorToast: true },
    onSettled: () => invalidateFor('office.create', qc),
  });
}

export function useUpdateOffice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      request,
    }: {
      id: number;
      request: OrganizationOfficeRequestDto;
    }) => officeApiClient.update(id, request),
    meta: { successMessage: 'Office Updated!', suppressErrorToast: true },
    onSuccess: (office) =>
      qc.setQueryData(queryKeys.organization.officeDetail(office.id), office),
    onSettled: (_data, _err, { id }) =>
      invalidateFor('office.update', qc, { id }),
  });
}

export function useDeleteOffice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (office: OrganizationOfficeDto) =>
      officeApiClient.delete(office.id).then(() => office),
    meta: { successMessage: 'Office Deleted!' },
    onMutate: (office: OrganizationOfficeDto) =>
      optimisticList(qc, {
        queryKey: queryKeys.organization.offices(),
        op: 'remove',
        entity: office,
        matchBy: (a, b) => a.id === b.id,
      }),
    onError: (_err, _office, ctx) => rollbackList(qc, ctx),
    onSettled: () => invalidateFor('office.delete', qc),
  });
}
